import { memo } from 'react';
import PropTypes from 'prop-types';

import Button from '../../../Button.jsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * "n of N" with a previous and a next control, right-aligned.
 *
 * Shared by the two AI widgets, which both show **one** item at a time on
 * purpose: the engine has already prioritised, and handing the reader a stack
 * of five is not trusting it. The counter is what says there are others —
 * without it, paging controls on a single card look broken.
 *
 * Chrome above the content rather than below it: stacked underneath, the
 * controls end up at the bottom of a card whose height varies with the text,
 * so they move every time the item changes.
 *
 * @param {object} props
 * @param {number} props.index Zero-based position of the visible item.
 * @param {number} props.count Total items.
 * @param {(delta: number) => void} props.onStep Called with -1 or 1.
 * @param {string} [props.previousLabel='Previous']
 * @param {string} [props.nextLabel='Next']
 * @param {string} [props.label] Replaces "n of N". A carousel that shows
 *   several cards at once is not on item 2 of 4 — it is on a page of them —
 *   so it says what it is actually showing instead.
 */
function WidgetCarouselNav({
  index,
  count,
  onStep,
  previousLabel = 'Previous',
  nextLabel = 'Next',
  label = '',
}) {
  if (count <= 1) return null;

  return (
    <div className="flex items-center justify-end gap-1.5">
      <span className="text-[11px] text-content-muted">
        {label || `${index + 1} of ${count}`}
      </span>
      <Button
        variant="tertiary"
        size="sm"
        leftIcon={ChevronLeft}
        aria-label={previousLabel}
        onClick={() => onStep(-1)}
      />
      <Button
        variant="tertiary"
        size="sm"
        leftIcon={ChevronRight}
        aria-label={nextLabel}
        onClick={() => onStep(1)}
      />
    </div>
  );
}

WidgetCarouselNav.propTypes = {
  index: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  onStep: PropTypes.func.isRequired,
  previousLabel: PropTypes.string,
  nextLabel: PropTypes.string,
  label: PropTypes.string,
};

export default memo(WidgetCarouselNav);
